

import { useCallback, useMemo } from 'react'
import { 
  useGetEmployeesQuery as getEmployeesQuery, 
  useGetEmployeeQuery as getEmployeeQuery, 
  useGetCustomersQuery as getCustomersQuery, 
  useGetCustomerQuery as getCustomerQuery, 
  useGetWorkTimes2Query as getWorkTimes2Query,
  useGetCustomersQuery,
  useGetWorkTimeSignaturesQuery} from '../../../api/plamo-api'
import { Link, useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { ActionTableState } from '../action-table/action-table'
import { Skeleton } from 'antd'
import { IconPencilSolidFull } from '../../../icon'

const calcDuration = (wt: any, useEmployeeTimes: boolean) => {
  const diff = (dayjs(useEmployeeTimes ? wt.endTimeEmployee : wt.endTime).valueOf() - dayjs(useEmployeeTimes ? wt.startTimeEmployee : wt.startTime).valueOf()) / 1000 / 60;
  return ("00" + (diff / 60).toFixed(2).replace('.', ',')).substr(-5);
}

const SubstitionCustomerLabel = ({ record }) => {
  const { data: customers } = useGetCustomersQuery({ f: JSON.stringify({ firstName: { c: "EQ", v: record.substitutionCustomerFirstName }, lastName: { c: "EQ", v: record.substitutionCustomerLastName } })}, { skip: !record.substitute }) as any;

  return <>
    { customers?.items?.length > 0 && <Link to={`/customer/${customers.items[0].id}`}>{record.substitutionCustomerFirstName} {record.substitutionCustomerLastName} (Vertretung)</Link> }
    { (record.substitute && customers?.items?.length === 0) ? <>{record.substitutionCustomerFirstName} {record.substitutionCustomerLastName} (Vertretung, <strong>Kunde nicht gefunden!</strong>)</> : <></> }
    { !record.substitute && <em>Keine Auswahl</em> }
  </>
}

export const SignatureImage = ({ record }) => {
  const { data: signatures, isLoading } = useGetWorkTimeSignaturesQuery({ f: JSON.stringify({ "workTime.id": { c: "EQ", v: record.id } }) }) as any;

  return <>
    { isLoading && <Skeleton.Node style={{ height: '3em' }} active />}
    { !isLoading && (signatures?.items?.length > 0 ? <img src={signatures.items[0].signatureImage} style={{ height: "3em"}} alt="Unterschrift" /> : <strong>Unterschrift fehlt!</strong>) }
  </>
}

function WorktimeTable({ preselectedEmployeeId, preselectedCustomerId, preselectedEmployeeClosureId, 
      useEmployeeTimes = false,
      begin, end }: any) {
  const navigate = useNavigate()
  
  const columns = useMemo(() => [
    {
      title: "Id",
      dataIndex: "id",
      sorter: true,
      filter: true,
      type: 'int',
    },
    {
      title: "Bearb.",
      dataIndex: "updatedBy",
      render: (_, record) => record.updatedBy ? <IconPencilSolidFull /> : null
    },
    ...(preselectedCustomerId ? [] : [
      {
        title: "Kunde",
        dataIndex: "customerId",
        type: 'reference',
        typeAttrs: {
          referenceList: () => getCustomersQuery({ o: 0, c: 1000, st: JSON.stringify({ name: 'asc' }), f: JSON.stringify({}) }),
          referenceSingle: id => getCustomerQuery({ id }),
          referenceDisplayString: customer => `${customer.firstName} ${customer.lastName}`,
          referenceDisplayStringNoSelect: record => <SubstitionCustomerLabel record={record} />,
          referenceLabel: customer => <Link to={`/customer/${customer.id}`}>{customer.firstName} {customer.lastName}</Link>,
          referenceGetId: customer => customer.id
        },
        sorter: true,
      },
      {
        title: "Anderere Tätigkeit",
        dataIndex: "otherTask",
        sorter: true,
        filter: true
      }
    ]),
    ...(preselectedEmployeeId ? [] : [
      {
        title: "Mitarbeiter",
        dataIndex: "employeeId",
        type: 'reference',
        typeAttrs: {
          referenceList: () => getEmployeesQuery({ o: 0, c: 1000, st: JSON.stringify({ name: 'asc' }), f: JSON.stringify({}) }),
          referenceSingle: id => getEmployeeQuery({ id }),
          referenceDisplayString: employee => `${employee.firstName} ${employee.lastName}`,
          referenceLabel: employee => <Link to={`/employee/${employee.id}`}>{employee.firstName} {employee.lastName}</Link>,
          referenceGetId: employee => employee.id
        },      
      },
    ]
    ),
    {
      title: "Datum",
      dataIndex: useEmployeeTimes ? "startTimeEmployee" : "startTime",
      type: 'date',
      sorter: true,
    },
    {
      title: "Beginn",
      dataIndex: useEmployeeTimes ? "startTimeEmployee" : "startTime",
      render: v => dayjs(v).format('HH:mm')
    },{
      title: "Ende",
      dataIndex: useEmployeeTimes ? "endTimeEmployee" : "endTime",
      render: v => dayjs(v).format('HH:mm')
    },
    {
      title: "Dauer",
      dataIndex: "duration",
      render: (_, record) => calcDuration(record, useEmployeeTimes)
    },
    {
      title: "Budget",
      dataIndex: "budget",
      render: v => {
        if(v === "supportAmount") {
          return "EB"
        } else if(v === "absenceCare") {
          return "VHP"
        } else if(v === "conversion") {
          return "U"
        } else if(v === "otherBudgetAmount") {
          return "SONST"
        }
        return v;
      }
    },
    {
      title: "Abw. Arbeitszeit",
      dataIndex: "duration",
      render: (_, record) => calcDuration(record, true) !== calcDuration(record, false) ? 'Ja' : 'Nein'
    },
    {
      title: "KM hin",
      dataIndex: "distanceFromManual",
      render: (v) => v?.toFixed(1).replace('.', ',')
    },
    {
      title: "KM zurück",
      dataIndex: "distanceToManual",
      render: (v) => v?.toFixed(1).replace('.', ',')
    },
    {
      title: "Einkauf",
      dataIndex: "distanceShopping",
      render: (v) => v?.toFixed(1).replace('.', ',')
    },
    {
      title: "Signatur",
      dataIndex: "signatureImage",
      render: (_, record) => <SignatureImage record={record} />
    }
  ], [preselectedEmployeeId, preselectedCustomerId, useEmployeeTimes])

  const applyPreselectedToFilter = useCallback(f => {
    const obj = JSON.parse(f);
    obj["changeRequestForId"] = {
      c: "EQ",
      v: null
    }

    if(preselectedCustomerId) {
      obj["customerId"] = {
        c: "EQ",
        v: preselectedCustomerId
      }
    }   
    if(preselectedEmployeeClosureId === null) {
      obj["employeeClosureId"] ={
        c: "EQ",
        v: null
      }
    }
    if(preselectedEmployeeClosureId) {
      obj["employeeClosureId"] ={
        c: "EQ",
        v: preselectedEmployeeClosureId
      }
    }
    if(begin && end) {
      obj["startTime"] = {
        c: "BTW",
        v: dayjs(begin).utc().format('YYYY-MM-DDTHH:mm:ss') + '.000', 
        v2: dayjs(end).utc().format('YYYY-MM-DDTHH:mm:ss') + '.000', 
        t: 'DATE'
      }
    }
    if(preselectedEmployeeId) {
      obj["employeeId"] = {
        c: "EQ",
        v: preselectedEmployeeId
      }
    }
    return JSON.stringify(obj);
  }, [preselectedEmployeeId, preselectedCustomerId, preselectedEmployeeClosureId, begin, end])

  const newQueryParams = useMemo(() => {
    const params = {
      preselectedCustomerId,
      preselectedEmployeeId,
      preselectedEmployeeClosureId
    }
    let mapped = Object.entries(params).filter(([k, v]) => v).map(([k, v]: [string, any]) => `${k}=${v}`).join('&')
    if(mapped.length) {
      mapped = `?${mapped}`
    }
    return mapped
  }, [preselectedCustomerId, preselectedEmployeeId, preselectedEmployeeClosureId])

  return (
    <>
    <div>
    <ActionTableState
          isParentLoading={false}
          buttonRow={{
            buttons: [
              {
                text: 'Neu',
                buttonType: 'primary',
                onClick: () => navigate(`/worktime/new${newQueryParams}`)
              },
            ]
          }}
          tableData={{
            defaultSortKey: useEmployeeTimes ? "startTimeEmployee" : "startTime",
            defaultSortDirection: 'desc',
            columns,
            dataSource: {
              pageableQueryEndpoint: ({ f, ...rest }) => getWorkTimes2Query({ f: applyPreselectedToFilter(f), ...rest }),
              queryResultMapping: (result) => result?.items
            },
            actions: [
              {
                caption: 'Bearbeiten',
                onClick: (item) => navigate(`/worktime/${item.id}`)
              },
            ]
          }}
        />
    </div>
    </>
    
  )
}

export default WorktimeTable